/**
 * Whether the desktop shell's native OS popups are actually reaching the
 * screen.
 *
 * `notifyNative` (transport.ts) used to swallow a failed `invoke("notify")`
 * outright — the comment there called it "not worth interrupting anything
 * for," true of any single failure, but wrong once failures are consistent.
 * A user whose OS notification permission is off (or was never granted) gets
 * every message by email and never learns why the desktop popup they expect
 * never shows up; the in-app bell still has the notification, so nothing else
 * looks broken. That gap needs a signal from the caller, since a Rust-side
 * `.show()` error cannot itself distinguish "permission denied" from a
 * transient daemon hiccup — see alerts.rs's `notify()`. Rather than guess, this
 * module counts failures and only flags a real pattern.
 *
 * Same shape as chime.ts's audio-state tracking: module-level state plus a
 * listener set, since the warning banner and whatever reports failures are
 * siblings in the component tree, not parent/child.
 */

const STREAK_THRESHOLD = 2;

let consecutiveFailures = 0;
let flagged = false;

const listeners = new Set<() => void>();

export function onNativeNotificationHealthChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce(): void {
  for (const listener of listeners) listener();
}

/** Called by notifyNative after every attempt, desktop-only. */
export function reportNativeNotificationOutcome(succeeded: boolean): void {
  if (succeeded) {
    // One success clears the streak — a single denied/offline blip earlier in
    // the session should not keep the warning showing once it is clearly
    // working again.
    if (consecutiveFailures > 0 || flagged) {
      consecutiveFailures = 0;
      flagged = false;
      announce();
    }
    return;
  }

  consecutiveFailures += 1;
  if (consecutiveFailures >= STREAK_THRESHOLD && !flagged) {
    flagged = true;
    announce();
  }
}

/** Whether enough consecutive failures have happened this session to warn about it. */
export function isNativeNotificationFlagged(): boolean {
  return flagged;
}
